import type { ReadonlyJSONValue } from "@rocicorp/zero";
import type { ChatTool, ToolUseProps } from "../components/Chat/ChatTool";
import { Step } from "../components/Step";
import { Text } from "../components/Text";

export type BashData = {
  getStepColor: (stepName: string) => string | undefined;
};

function parseInput(toolUse: ToolUseProps["toolUse"]) {
  const v = toolUse.input;
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, ReadonlyJSONValue>)
    : null;
}

function str(v: ReadonlyJSONValue | undefined): string | undefined {
  return typeof v === "string" ? v : undefined;
}

function BashHeader({ toolUse, result, data, interrupted }: ToolUseProps) {
  const input = parseInput(toolUse);
  const stepName = str(input?.step);
  const color = stepName
    ? (data as BashData | undefined)?.getStepColor(stepName)
    : undefined;

  return (
    <Step
      color={color ? `var(--${color}-600)` : "var(--gray-600)"}
      name={stepName}
      done={!!result}
      interrupted={interrupted}
      pastParticiple={str(input?.completedDescription) ?? "Done"}
      inProgress={
        str(input?.activeDescription)
          ? `${str(input?.activeDescription)}…`
          : "Working…"
      }
    />
  );
}

function BashBody({ toolUse, result }: ToolUseProps) {
  const input = parseInput(toolUse);
  const command = str(input?.command);

  return (
    <Text variant="code-small" color="var(--text-secondary)">
      {command && (
        <pre
          style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {command}
        </pre>
      )}
      {result && (
        <pre
          style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {JSON.stringify(result.output, null, 2)}
        </pre>
      )}
    </Text>
  );
}

export const bash = (data: BashData): ChatTool => ({
  name: "bash",
  Header: BashHeader,
  Body: BashBody,
  data,
});
